import { Box, Typography } from "@mui/material"
import type { FC } from "react"
import { useFormContext } from "react-hook-form"

import type { InventoryAdjustmentQuantityFormValues } from "./schemas"

import { FormSelect } from "@/components/UiKit/FormControls/FormSelect"
import { FormTextField } from "@/components/UiKit/FormControls/FormTextField"
import {
  ALLOWED_TYPE_LABEL_IMAGES_WITH_PDF_AND_DOC,
  IMAGE_MIME_TYPES_WITH_PDF_AND_DOC,
} from "@/components/upload/constants"
import FileInput from "@/components/upload/FileInput"
import { InventoryAdjustmentReason } from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"

interface ReasonLabelProps {
  reason: string
  description: string
}

const ReasonLabel: FC<ReasonLabelProps> = ({ reason, description }) => (
  <>
    {reason}
    <span className='ml-1 text-gray-600'>({description})</span>
  </>
)

export const ADJUSTMENT_REASON_OPTIONS: Option<string>[] = [
  {
    value: InventoryAdjustmentReason.Damage,
    label: <ReasonLabel reason='Damage' description="Material can't be sold" />,
  },
  {
    value: InventoryAdjustmentReason.Samples,
    label: <ReasonLabel reason='Samples' description='Sent to sample room' />,
  },
  {
    value: InventoryAdjustmentReason.TollMixing,
    label: <ReasonLabel reason='Toll Mixing' description='Sent to manufacturer' />,
  },
  {
    value: InventoryAdjustmentReason.LostMaterial,
    label: <ReasonLabel reason='Lost Material' description="Missing / can't locate" />,
  },
  {
    value: InventoryAdjustmentReason.FoundMaterial,
    label: <ReasonLabel reason='Found Material' description='Not previously in system' />,
  },
  {
    value: InventoryAdjustmentReason.CustomerReturn,
    label: <ReasonLabel reason='Customer Return' description='Initiates receiving' />,
  },
  {
    value: InventoryAdjustmentReason.ExpiredMaterial,
    label: <ReasonLabel reason='Expired Material' description='Shelf life exceeded' />,
  },
  { value: InventoryAdjustmentReason.Other, label: "Other" },
]

export const InventoryAdjustmentQuantityDetailsForm: FC = () => {
  const { control } = useFormContext<InventoryAdjustmentQuantityFormValues>()

  return (
    <Box className='rounded-lg border border-gray-300 p-4'>
      <Typography variant='subtitle1' className='mb-4 font-bold'>
        3. Adjustment Details
      </Typography>
      <Box className='flex flex-col gap-y-4'>
        <FormSelect
          control={control}
          name='adjustmentReason'
          label='Adjustment Reason'
          required
          placeholder='Select one...'
          options={ADJUSTMENT_REASON_OPTIONS}
          MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
          data-testid='inventory-adjustment.quantity-details.reason-select'
        />

        <FormTextField
          control={control}
          name='comment'
          label='Comment'
          placeholder='Enter comment'
          multiline
          rows={4}
          data-testid='inventory-adjustment.quantity-details.comment-field'
        />

        <FileInput<InventoryAdjustmentQuantityFormValues>
          name='document'
          allowedMimeTypes={IMAGE_MIME_TYPES_WITH_PDF_AND_DOC}
          allowedTypesLabel={ALLOWED_TYPE_LABEL_IMAGES_WITH_PDF_AND_DOC}
          uploadAreaTitle='Click to upload or drag and drop'
          data-testid='inventory-adjustment.quantity-details.document-upload'
        />
      </Box>
    </Box>
  )
}
